import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProgressData, ProgressData } from '../../utils/progressMigration';
import { QuizQuestionSelector, QuizQuestion } from '../../utils/QuizQuestionSelector';
import { AppBook } from '../../types/utils';
import QuizConfigComponent from './QuizConfig';
import QuizTaker from './QuizTaker';
import QuizResults from './QuizResults';
import '../QuizTaker.css';

interface QuizConfig {
  seed: number;
  questionCount: number;
  chapter: string;
  book: string;
  questionType: 'true_false' | 'multiple_choice';
}

interface QuizTakerContainerProps {
  book: AppBook;
  defaultQuestionType: 'true_false' | 'multiple_choice';
  selectedBook: string;
  selectedChapter: string;
  availableBooks: string[];
  availableChapters: string[];
  onBookChange: (bookId: string) => void;
  onChapterChange: (chapterId: string) => void;
}

enum QuizState {
  CONFIGURING,
  TAKING_QUIZ,
  SHOWING_RESULTS
}

const QuizTakerContainer: React.FC<QuizTakerContainerProps> = ({
  book,
  defaultQuestionType,
  selectedBook,
  selectedChapter,
  availableBooks,
  availableChapters,
  onBookChange,
  onChapterChange
}) => {
  const [quizState, setQuizState] = useState<QuizState>(QuizState.CONFIGURING);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<Record<string, string>>({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState<{ isCorrect: boolean; message: string; explanation: string } | null>(null);
  const [progress, setProgress] = useState<ProgressData>(() => {
    const initialProgress = getProgressData();
    console.log('QuizTakerContainer - Initializing progress state with:', initialProgress);
    return initialProgress;
  });
  const [selectedQuestionType, setSelectedQuestionType] = useState<'true_false' | 'multiple_choice'>(defaultQuestionType);
  const [quizConfig, setQuizConfig] = useState<QuizConfig>({
    seed: Math.floor(Math.random() * 1000000),
    questionCount: 10,
    chapter: selectedChapter,
    book: selectedBook,
    questionType: defaultQuestionType
  });
  const [questionCountInput, setQuestionCountInput] = useState<string>(quizConfig.questionCount.toString());
  const navigate = useNavigate();

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    console.log('QuizTakerContainer - Saving progress data:', progress);
    localStorage.setItem('quizProgress', JSON.stringify(progress));
  }, [progress]);

  // Parse URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const seed = urlParams.get('seed');
    const questionCount = urlParams.get('q');
    const chapter = urlParams.get('chapter');
    const book = urlParams.get('book');
    const questionType = urlParams.get('type');

    if (seed || questionCount || chapter || book || questionType) {
      const config: QuizConfig = {
        seed: seed ? parseInt(seed) : Math.floor(Math.random() * 1000000),
        questionCount: questionCount ? parseInt(questionCount) : 10,
        chapter: chapter || selectedChapter,
        book: book || selectedBook,
        questionType: (questionType as 'true_false' | 'multiple_choice') || defaultQuestionType
      };
      setQuizConfig(config);
      setQuestionCountInput(config.questionCount.toString());

      // Update parent component state
      if (config.book !== selectedBook) onBookChange(config.book);
      if (config.chapter !== selectedChapter) onChapterChange(config.chapter);
      if (config.questionType !== selectedQuestionType) setSelectedQuestionType(config.questionType);
    }
  }, [selectedBook, selectedChapter, defaultQuestionType, onBookChange, onChapterChange, selectedQuestionType]);

  // Get progress stats
  const getProgressStats = () => {
    const totalAnswered = Object.keys(progress).length;
    const totalCorrect = Object.values(progress).filter(p => p.mostRecentlyCorrect).length;
    const totalIncorrect = totalAnswered - totalCorrect;
    const accuracy = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
    const studyBankCount = Object.keys(progress).length;

    return {
      totalAnswered,
      totalCorrect,
      totalIncorrect,
      accuracy,
      studyBankCount
    };
  };

  // Generate quiz questions based on configuration
  const generateQuizQuestions = () => {
    const selector = new QuizQuestionSelector(book);
    
    const config: QuizConfig = {
      seed: quizConfig.seed,
      questionCount: quizConfig.questionCount,
      chapter: quizConfig.chapter,
      book: quizConfig.book,
      questionType: quizConfig.questionType
    };

    try {
      const result = selector.selectQuestions(config);
      console.log(`Generated quiz with ${result.questions.length} questions out of ${result.totalAvailable} available`);
      console.log(`Quiz config:`, config);
      setQuizQuestions(result.questions);
    } catch (error) {
      console.error('Error generating quiz questions:', error);
      setQuizQuestions([]);
    }
  };

  // Start quiz
  const handleStartQuiz = () => {
    generateQuizQuestions();
    setQuizState(QuizState.TAKING_QUIZ);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setShowFeedback(false);
    setCurrentFeedback(null);
  };

  // Handle answer selection
  const handleAnswer = (answer: string) => {
    const currentQuestion = quizQuestions[currentQuestionIndex];
    if (!currentQuestion) return;

    let isCorrect: boolean;
    if (currentQuestion.type === 'multiple_choice' && currentQuestion.options) {
      // For multiple choice questions, correctAnswer already contains the actual answer text
      // (converted from letter key in QuizQuestionSelector.extractCorrectAnswer)
      if (currentQuestion.correctAnswer && typeof currentQuestion.correctAnswer === 'string') {
        isCorrect = answer === currentQuestion.correctAnswer;
      } else {
        isCorrect = false;
      }
    } else {
      // For true/false questions, convert string answer to boolean for comparison
      const userAnswerBoolean = answer === 'true';
      
      // Handle both string and boolean correct answers
      let correctAnswerBoolean: boolean;
      if (typeof currentQuestion.correctAnswer === 'string') {
        const correctAnswer = currentQuestion.correctAnswer;
        const correctAnswerLower = correctAnswer.toLowerCase();
        correctAnswerBoolean = correctAnswerLower === 'true';
      } else {
        correctAnswerBoolean = currentQuestion.correctAnswer as boolean;
      }
      
      isCorrect = userAnswerBoolean === correctAnswerBoolean;
    }

    const feedback = {
      isCorrect,
      message: isCorrect ? 'Great job!' : 'That\'s not quite right.',
      explanation: currentQuestion.explanation || ''
    };

    setUserAnswers(prev => ({ ...prev, [currentQuestion.id]: answer }));
    setShowFeedback(true);
    setCurrentFeedback(feedback);

    // Update progress
    const progressKey = currentQuestion.id;
    setProgress(prev => ({
      ...prev,
      [progressKey]: {
        bookId: quizConfig.book,
        chapterId: quizConfig.chapter,
        mostRecentlyCorrect: isCorrect,
        questionId: currentQuestion.id,
        questionData: {
          question: currentQuestion.question,
          type: currentQuestion.type,
          options: currentQuestion.options,
          correctAnswer: currentQuestion.correctAnswer,
          explanation: currentQuestion.explanation
        },
        attempts: [
          {
            userAnswer: answer,
            timestamp: Date.now(),
            correct: isCorrect
          }
        ]
      }
    }));
  };

  // Navigation handlers
  const handleNext = () => {
    if (currentQuestionIndex < quizQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setShowFeedback(false);
      setCurrentFeedback(null);
    } else {
      // Quiz complete
      setQuizState(QuizState.SHOWING_RESULTS);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setShowFeedback(false);
      setCurrentFeedback(null);
    }
  };

  const handleSkip = () => {
    console.log(`Skip clicked for question ${currentQuestionIndex + 1} of ${quizQuestions.length}`);
    const currentQuestion = quizQuestions[currentQuestionIndex];
    if (currentQuestion) {
      console.log(`Marking question "${currentQuestion.question.substring(0, 50)}..." as skipped`);
      setUserAnswers(prev => ({ ...prev, [currentQuestion.id]: 'skipped' }));
      // Move to next question or finish quiz
      if (currentQuestionIndex < quizQuestions.length - 1) {
        console.log(`Moving to next question: ${currentQuestionIndex + 1} -> ${currentQuestionIndex + 2}`);
        setCurrentQuestionIndex(prev => prev + 1);
        setShowFeedback(false);
        setCurrentFeedback(null);
      } else {
        console.log('Quiz complete, showing results');
        setQuizState(QuizState.SHOWING_RESULTS);
      }
    } else {
      console.log('No current question found for skip');
    }
  };

  // Quiz management handlers
  const handleRetryQuiz = () => {
    setQuizState(QuizState.TAKING_QUIZ);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setShowFeedback(false);
    setCurrentFeedback(null);
  };

  const handleConfigureNewQuiz = () => {
    setQuizState(QuizState.CONFIGURING);
  };

  const handleClearProgress = () => {
    if (window.confirm('Are you sure you want to clear all progress? This cannot be undone.')) {
      setProgress({});
      localStorage.removeItem('quizProgress');
    }
  };

  // Calculate score
  const calculateScore = () => {
    let correct = 0;
    let total = 0;

    quizQuestions.forEach(question => {
      const userAnswer = userAnswers[question.id];
      if (userAnswer && userAnswer !== 'skipped') {
        total++;
        
        // Use the same logic as handleAnswer for consistency
        let isCorrect: boolean;
        if (question.type === 'multiple_choice' && question.options) {
          if (question.correctAnswer && typeof question.correctAnswer === 'string') {
            isCorrect = userAnswer === question.correctAnswer;
          } else {
            isCorrect = false;
          }
        } else {
          // For true/false questions, convert string answer to boolean for comparison
          const userAnswerBoolean = userAnswer === 'true';
          isCorrect = userAnswerBoolean === question.correctAnswer;
        }
        
        if (isCorrect) {
          correct++;
        }
      }
    });

    return { correct, total };
  };

  const stats = getProgressStats();

  // Render based on current state
  switch (quizState) {
    case QuizState.CONFIGURING:
      return (
        <QuizConfigComponent
          quizConfig={quizConfig}
          questionCountInput={questionCountInput}
          availableChapters={availableChapters}
          bookTitle={book.title}
          onConfigChange={setQuizConfig}
          onQuestionCountInputChange={setQuestionCountInput}
          onStartQuiz={handleStartQuiz}
          onBack={() => navigate('/')}
        />
      );

    case QuizState.SHOWING_RESULTS:
      return (
        <QuizResults
          score={calculateScore()}
          stats={stats}
          onRetryQuiz={handleRetryQuiz}
          onConfigureNewQuiz={handleConfigureNewQuiz}
          onClearProgress={handleClearProgress}
        />
      );

    case QuizState.TAKING_QUIZ:
      return (
        <QuizTaker
          quizQuestions={quizQuestions}
          currentQuestionIndex={currentQuestionIndex}
          userAnswers={userAnswers}
          showFeedback={showFeedback}
          currentFeedback={currentFeedback}
          quizConfig={quizConfig}
          stats={stats}
          bookTitle={book.title}
          onAnswer={handleAnswer}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onSkip={handleSkip}
          onConfigureQuiz={handleConfigureNewQuiz}
        />
      );

    default:
      return null;
  }
};

export default QuizTakerContainer; 